import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/apiClient';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingState from '../components/LoadingState';
import { getUserFriendlyError } from '../utils/apiErrors';

const LikesPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();

    const fetchLikes = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/users/likes');
            if (response?.forbidden) {
                setError('This feature is available with an eligible premium plan.');
            } else {
                const usersArray = response?.data || response;
                setUsers(Array.isArray(usersArray) ? usersArray : []);
            }
        } catch (err) {
            setError(getUserFriendlyError(err, 'We could not load your likes. Please try again.'));
        } finally {
            setLoading(false);
        }
    }, []);

    const handleChat = (targetUid) => {
        if (!currentUser?.uid) {
            setError('Your account details are still loading. Please try again in a moment.');
            return;
        }
        const chatId = [currentUser.uid, targetUid].sort().join('-');
        navigate(`/chat/${chatId}`);
    };

    useEffect(() => {
        fetchLikes();
    }, [fetchLikes]);

    if (loading) {
        return <LoadingState spinnerOnly />;
    }

    if (error) {
        return <div className="state-panel state-error mx-4 my-8 text-center" role="alert"><p>{error}</p><button type="button" onClick={fetchLikes} className="app-primary-button mt-4 rounded-full px-5 py-2.5 text-sm font-semibold text-white">Try again</button></div>;
    }

    return (
        <div className="page-container likes-page mx-auto w-full max-w-4xl p-4 sm:p-6">
            <div className="mb-6"><p className="page-eyebrow">A LITTLE SPARK</p><h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Who likes you</h1><p className="mt-2 text-sm text-gray-500">See who is interested and start a conversation.</p></div>
            {users.length > 0 ? (
                <ul className="space-y-4">
                    {users.map(user => (
                        <li
                            key={user._id || user.uid}
                            className="like-card flex items-center justify-between gap-4 rounded-2xl p-4 sm:p-5"
                        >
                            <div className="min-w-0">
                                <p className="truncate font-semibold text-gray-900">{user.name}</p>
                                <p className="text-gray-600 text-sm">{user.bio}</p>
                            </div>
                            <button
                                onClick={() => handleChat(user.uid)}
                                className="app-primary-button flex-shrink-0 rounded-full px-5 py-2.5 text-sm font-semibold text-white"
                            >
                                Chat
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="empty-state rounded-2xl p-10 text-center"><p className="text-lg font-semibold text-gray-800">No likes to show just yet</p><p className="mt-2 text-sm text-gray-500">Keep your profile fresh and check back soon.</p></div>
            )}
        </div>
    );
};

export default LikesPage;
